import {
  Box,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo, useState } from "react";
import { useGetMenuQuery } from "store/apis/MenusApi";
import { EditSkeleton } from "components/admin/admin.overlays";
import { useGetMealAddOnsQuery, useGetPendingMealsQuery } from "store/apis/MealsApi";
import {
  useGetResidentActiveMealsByIdQuery,
  useGetResidentInfoByIdQuery,
  useGetResidentsAllQuery,
} from "store/apis/ResidentsApi";
import { useGetDiningAreasQuery } from "store/apis/DiningAreasApi";
import { useSelector, useDispatch } from "react-redux";
import { selectPermissions } from "store/slices/appSlice/AppSlice";
import { setSelectedMeal } from "store/slices/orderSlice/OrderSlice";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import InfoModal from "components/shared/infoModal/InfoModal";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import OrderConfirmModal from "components/shared/orderConfirmModal/OrderConfirmModal";
import {
  useAddResidentOrderMutation,
  useDeleteOrderMutation,
  useGetFutureOrderQuery,
} from "store/apis/OrdersApi";
import ReasonModal from "components/shared/reasonModal/ReasonModal";
import DeleteModal from "components/shared/deleteModal/DeleteModal";
import { WarningText } from "components/shared/WarningText";
import { BackButton } from "components/shared/BackButton";
import ResidentDetailSection from "components/takeOrder/order/ResidentDetailSection";
import OrderMenuDetails from "components/shared/orderMenuDetails/OrderMenuDetails";
import { getMealSetupStatus, findMealBySetupId } from "utils/mealSetups";

export default function AddEditResidentOrder() {
  const { residentId, tableSideSetupId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const isEditing = !!orderId;
  const currentLocation = useLocation();
  const queuedSetups = currentLocation.state?.queuedSetups ?? [];
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const permissions = useSelector(selectPermissions);
  const allowMealAddons = permissions?.includes("Print Meal Addons");
  const isAutoSelectItems = permissions?.includes("All Items Selected by Default");

  const [selectedDiningAreaId, setSelectedDiningAreaId] = useState("");
  const [selectedTableName, setSelectedTableName] = useState("");
  const [outOfBuilding, setOutOfBuilding] = useState(false);
  const [declinedMeal, setDeclinedMeal] = useState(false);
  const [orderInfo, setOrderInfo] = useState({
    menuItems: [],
    alternateItems: [],
    order: {},
  });

  const [selectedSubRecipes, setSelectedSubRecipes] = useState([]);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalTitle, setReasonModalTitle] = useState(false);
  const [isSubmitAfterReason, setIsSubmitAfterReason] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [addlCharges, setAddlCharges] = useState(0.0);
  const [selectedAddlCharges, setSelectedAddlCharges] = useState([]);
  const [customChargeText, setCustomChargeText] = useState("");
  // once the order is being deleted, the invalidated query refetch would 404 before we navigate away - ignore it
  const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);

  const {
    data: orderData,
    isFetching: fetchingOrder,
    isError: fetchOrderError,
    error: fetchOrderErrorMsg,
  } = useGetFutureOrderQuery({ orderId, isAdmin: true }, {
    refetchOnMountOrArgChange: true,
    skip: !isEditing || isFinalizingOrder,
  });

  useEffect(() => {
    if (fetchOrderError && !fetchingOrder && !isFinalizingOrder) {
      enqueueSnackbar(fetchOrderErrorMsg?.data?.messages?.[0] ?? "Unable to load order.", { variant: "error" });
      navigate("/ordermanagement/residents");
      return;
    }
    // guards against a stale/mismatched orderId query param (order view can be reached from an external app)
    if (orderData && !fetchingOrder && !isFinalizingOrder && orderData?.order?.residentId !== +residentId) {
      enqueueSnackbar("This order does not belong to the selected resident.", { variant: "error" });
      navigate("/ordermanagement/residents");
    }
  }, [fetchOrderError, fetchingOrder, orderData]);

  // restoring an in-progress order also restores the OOB/declined-meal flags — the rest of the
  // order's menu selections are restored inside OrderMenuDetails
  useEffect(() => {
    if (isEditing && orderData) {
      setDeclinedMeal(!!orderData?.order?.declinedMeal);
      setOutOfBuilding(!!orderData?.order?.outOfBuilding);
    }
  }, [orderData]);

  // order management deals with past meals too, so pendingMeals must include them or a past meal's setup won't resolve
  const { data: pendingMeals } = useGetPendingMealsQuery(true);

  const { data: diningAreas, isFetching: fetchingAreas } = useGetDiningAreasQuery();

  const { effectiveMenuId, effectiveSetupId, setupWarning, currentMealName, currentMealDiningDate, currentMealId } = useMemo(() => {
    if (!selectedDiningAreaId) return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: "", currentMealName: null, currentMealDiningDate: null, currentMealId: null };

    const diningAreaName = diningAreas?.find(a => a.id === selectedDiningAreaId)?.name;

    const selectedMeal = queuedSetups?.length
      ? findMealBySetupId(pendingMeals, queuedSetups[0]?.id)
      : null;

    // editing an existing order — resolved by the order's own stored setup id, so it doesn't need to
    // still be the currently active meal (matters on a fresh/direct page load with no nav state)
    const editingOrderMeal = isEditing
      ? pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === orderData?.order?.tableSideSetupId))
      : null;

    const targetMeal = isEditing
      ? (editingOrderMeal ?? pendingMeals?.find(m => m.isActive))
      : (selectedMeal ?? pendingMeals?.find(m => m.isActive));

    if ((queuedSetups?.length && selectedMeal) || editingOrderMeal) {
      const { setup, isValid, message } = getMealSetupStatus(
        pendingMeals,
        targetMeal,
        selectedDiningAreaId,
        diningAreaName,
        queuedSetups?.length ? queuedSetups : undefined,
        true
      );
      if (setup?.menuId) {
        return { effectiveMenuId: setup.menuId, effectiveSetupId: setup.id, setupWarning: isValid ? "" : message, currentMealName: targetMeal?.mealName ?? null, currentMealDiningDate: targetMeal?.diningDate ?? null, currentMealId: targetMeal?.mealId ?? null };
      }
      if (!isValid) {
        return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: message, currentMealName: targetMeal?.mealName ?? null, currentMealDiningDate: targetMeal?.diningDate ?? null, currentMealId: targetMeal?.mealId ?? null };
      }
    }

    if (!pendingMeals?.length) return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: "", currentMealName: null, currentMealDiningDate: null, currentMealId: null };

    const { setup, isValid, message } = getMealSetupStatus(
      pendingMeals,
      targetMeal,
      selectedDiningAreaId,
      diningAreaName
    );

    return {
      effectiveMenuId: setup?.menuId ?? null,
      effectiveSetupId: setup?.id ?? null,
      setupWarning: isValid ? "" : message,
      currentMealName: targetMeal?.mealName ?? null,
      currentMealDiningDate: targetMeal?.diningDate ?? null,
      currentMealId: targetMeal?.mealId ?? null,
    };
  }, [selectedDiningAreaId, queuedSetups, pendingMeals, diningAreas, isEditing, orderData?.order?.tableSideSetupId]);

  useEffect(() => {
    if (currentMealName) {
      const label = currentMealDiningDate
        ? `${currentMealName} (${dayjs(currentMealDiningDate).format('ddd [-] MM/DD/YYYY')})`
        : currentMealName;
      dispatch(setSelectedMeal(label));
    }
  }, [currentMealName, currentMealDiningDate]);

  useEffect(() => {
    return () => dispatch(setSelectedMeal("")); //reset title bar on unmount
  }, []);

  // freeze setup/area for the query — only changes when effectiveMenuId changes, not on every area switch
  const stableQueryParams = useMemo(() => ({
    setupId: effectiveSetupId ?? tableSideSetupId,
    diningAreaId: selectedDiningAreaId,
  }), [effectiveMenuId]);

  // true while effectiveMenuId/pendingMeals are still resolving — treat as loading, not "no menu found"
  const isMenuQuerySkipped = !stableQueryParams.diningAreaId || !residentId || !stableQueryParams.setupId;

  const { data: menuInfo, isFetching: fetchingMenu } = useGetMenuQuery(
    {
      residentId: residentId,
      setupId: stableQueryParams.setupId,
      diningAreaId: stableQueryParams.diningAreaId,
      isGuest: false,
      includeDiningAreas: true,
      guestResidentId: 0,
      guestName: "",
      guestBillingTypeId: 0,
    },
    { skip: isMenuQuerySkipped, refetchOnMountOrArgChange: true }
  );

  const { data: mealAddons, isFetching: fetchingAddons } =
    useGetMealAddOnsQuery(
      {
        residentId: residentId,
        setupId: stableQueryParams.setupId,
        diningAreaId: stableQueryParams.diningAreaId,
        isGuest: false,
      },
      { skip: !residentId || !stableQueryParams.setupId || !stableQueryParams.diningAreaId, refetchOnMountOrArgChange: true }
    );

  const {
    data: residents,
    isFetching: fetchingResidents,
  } = useGetResidentsAllQuery();

  const showPhoto = !!residents?.find(
    (resident) => resident.id == residentId
  )?.photoOnTrayCard;

  const { data: activeMeals } =
    useGetResidentActiveMealsByIdQuery(residentId, {
      refetchOnMountOrArgChange: true,
      skip: !residentId,
    });

  const { data: residentDetail, isFetching: fetchingResidentDetail } =
    useGetResidentInfoByIdQuery(
      { residentId, setupId: tableSideSetupId },
      { skip: !residentId || !tableSideSetupId, refetchOnMountOrArgChange: true }
    );

  const isResidentLoading = fetchingResidentDetail || fetchingAreas || fetchingResidents || fetchingOrder;
  const isMenuLoading = fetchingMenu || fetchingAddons || isMenuQuerySkipped;

  // normalizes residentDetail field names to match menuInfo shape for the info table
  const residentInfo = useMemo(() => {
    if (!isEditing && residentDetail) {
      return {
        dietName: residentDetail.diet,
        dietTextureName: residentDetail.dietTexture,
        dietOther: residentDetail.dietOther,
        liquidConsistency: residentDetail.liquidConsistency,
        fluidRestriction: residentDetail.fluidRestrictions,
        dislikes: residentDetail.dislikes,
        allergies: residentDetail.allergies,
        adaptiveEquipment: residentDetail.adaptiveEquipment,
        notes: residentDetail.notes ? [residentDetail.notes] : [],
        dietPrecautionsNote: residentDetail.dietPrecautionsNote,
        isClearLiquid: residentDetail.isClearLiquid,
        isFullLiquid: residentDetail.isFullLiquid,
        isLiquidized: residentDetail.isLiquidized,
        mealPlans: residentDetail.mealPlans,
        mealCostUsage: residentDetail.mealCostUsage,
        mealCountUsage: residentDetail.mealCountUsage
      };
    }
    if (isEditing) {
      // storedMenuInfo fields are available immediately; liquid flags come from menuInfo once loaded, merge with resident detail to fill gaps as needed
      return {
        dietName: orderData?.dietName || residentDetail?.diet,
        dietTextureName: orderData?.dietTextureName || residentDetail?.dietTexture,
        dietOther: orderData?.dietOther || residentDetail?.dietOther,
        liquidConsistency: orderData?.liquidConsistency || residentDetail?.liquidConsistency,
        fluidRestriction: orderData?.fluidRestriction || residentDetail?.fluidRestrictions,
        dislikes: orderData?.dislikes || residentDetail?.dislikes,
        allergies: orderData?.allergies || residentDetail?.allergies,
        adaptiveEquipment: orderData?.adaptiveEquipment || residentDetail?.adaptiveEquipment,
        notes: orderData?.notes ? orderData?.notes : residentDetail?.notes ? [residentDetail.notes] : [],
        dietPrecautionsNote: menuInfo?.dietPrecautionsNote,
        isClearLiquid: menuInfo?.isClearLiquid,
        isFullLiquid: menuInfo?.isFullLiquid,
        isLiquidized: menuInfo?.isLiquidized,
        mealPlans: residentDetail?.mealPlans,
        mealCostUsage: residentDetail?.mealCostUsage,
        mealCountUsage: residentDetail?.mealCountUsage,
      };
    }
    return menuInfo;
  }, [isEditing, residentDetail, orderData, menuInfo]);

  useEffect(() => {
    // set dining area as soon as residentDetail is available, unblocking the menu query
    if (!isEditing && residentDetail && diningAreas) {
      const diningAreaId =
        residentDetail.diningAreaId > 0
          ? residentDetail.diningAreaId
          : diningAreas[0]?.id || "";
      selectDiningArea(diningAreaId);
      selectTable(residentDetail.diningAreaId > 0 ? residentDetail.diningTable ?? "" : "");
    } else if (isEditing && orderData?.order?.diningAreaId) {
      selectDiningArea(orderData.order.diningAreaId);
      selectTable(orderData.order.tableName);
    }
  }, [residentDetail, diningAreas, orderData]);

  useEffect(() => {
    if (menuInfo) {
      const relatedSetupIds = new Set(queuedSetups?.map(s => s.id) ?? [effectiveSetupId]);
      let activeMeal;
      activeMeals?.days?.forEach((day) => {
        day?.meals?.forEach((meal) => {
          if (meal?.setupId == effectiveSetupId) {
            activeMeal = meal;
          }
        });
      });

      const orderAlreadyTaken = activeMeal?.orderTaken ||
        activeMeals?.days?.some((day) =>
          day?.meals?.some((meal) => relatedSetupIds.has(meal?.setupId) && meal?.orderTaken)
        );

      if (orderAlreadyTaken && !isEditing) {
        setModalTitle("ALERT");
        setModalText(
          "You have already taken an order for this person.\nThis order will be sent in addition to the original order.\nIf you want to reprint the original order, go to the 'Submitted' orders tab."
        );
        setInfoModalOpen(true);
      }
    }
  }, [menuInfo, activeMeals]);

  const handleModalClose = () => {
    setInfoModalOpen(false);
  };

  const selectDiningArea = (diningAreaId) => {
    setSelectedDiningAreaId(() => diningAreaId);
    setSelectedTableName("");
  };

  const selectTable = (tableName) => {
    setSelectedTableName(tableName);
    setOrderInfo((prev) => ({
      ...prev,
      order: {
        ...prev.order,
        tableName: tableName,
      },
    }));
  };

  const handleOutOfBuildingChange = () => {
    setOutOfBuilding((prev) => !prev);
    setDeclinedMeal(false);
  };

  const handleDeclinedMealChange = () => {
    setDeclinedMeal((prev) => !prev);
    setOutOfBuilding(false);
  };

  const handleSubmit = () => {
    // enforce dining area selection when not declining or out of building
    if (!selectedDiningAreaId && !outOfBuilding && !declinedMeal) {
      setModalTitle(""); // reset just in case other with title has been used
      setModalText("Dining Area cannot be blank.");
      setInfoModalOpen(true);
      return;
    }

    //guard clause for custom charge fields
    if (
      addlCharges > 100 ||
      (showAdditionalCharges &&
        (!customChargeText?.length || customChargeText?.length > 32))
    ) {
      return;
    }

    const tempMenuItems = [...orderInfo.menuItems];
    const tempAltItems = [...orderInfo.alternateItems];

    //force item selection when not declined/OOB
    if (
      !declinedMeal &&
      !outOfBuilding &&
      !tempMenuItems?.length &&
      !tempAltItems?.length
    ) {
      setModalTitle("");
      setModalText("Order must contain at least 1 item.");
      setInfoModalOpen(true);
      return;
    }

    tempMenuItems.forEach((item) => {
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId === item.itemId && !sub.isAlternateItem
      );
      item["subItemList"] = [...subRecipes];
    });

    tempAltItems.forEach((item) => {
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId === item.itemId && sub.isAlternateItem
      );
      item["subItemList"] = [...subRecipes];
    });

    //update fields we removed from onChange for performance reasons (large object re-writes)
    //and put menu/alt items back with selected subrecipes bound to parent
    setOrderInfo((prev) => ({
      ...prev,
      menuItems: [...tempMenuItems],
      alternateItems: [...tempAltItems],
      order: {
        ...prev.order,
        additionalCharges: addlCharges,
        additionalChargeNote: customChargeText,
        declinedMeal: declinedMeal,
        diningAreaId: selectedDiningAreaId || 0, //fallback for declined/OOB (call fails as null), otherwise selection is enforced anyway
        // stamped from effectiveSetupId (not the frozen menu-query setupId) so switching dining areas under the same menu still submits the matching setup
        tableSideSetupId: effectiveSetupId ?? orderData?.order?.tableSideSetupId ?? tableSideSetupId,
        guest: null,
        guestBillingTypeId: 0,
        guestTypeId: 0,
        employeeId: null,
        note: noteText,
        outOfBuilding: outOfBuilding,
        tableName: selectedTableName || "",
        orderCharges: [
          ...selectedAddlCharges.map((charge) => {
            return {
              ...charge,
              //needs to match held/edited order submission schema
              facilityChargeTypeId: charge.id,
              chargeName:
                charge.chargeTypeid === 6
                  ? charge.chargeTypeText
                  : charge.chargeTypeName,
              price: charge.price
            };
          }),
        ],
        residentId: +residentId,
        adaptiveEquipment: prev.adaptiveEquipment ?? "",
        allergies: prev.allergies ?? "",
        fluidRestriction: prev.fluidRestriction ?? "",
        mealAddons: mealAddons?.join("; ") ?? "",
      },
    }));

    // run this after order has been updated to short circuit confirm modal
    if (outOfBuilding || declinedMeal) {
      setReasonModalTitle(outOfBuilding ? "Out of Building" : "Declined Meal");
      setReasonModalOpen(true);
      return;
    }

    setConfirmModalOpen(true);
  };

  const [addNewOrder, { isSuccess: orderSuccess, isError: orderError }] =
    useAddResidentOrderMutation();
  const [deleteOrder, { isSuccess: deleteOrderSuccess }] =
    useDeleteOrderMutation();

  useEffect(() => {
    if (orderSuccess && !orderError) {
      if (isEditing) {
        deleteOrder({ orderId: orderData?.order?.id, isAdmin: true });
      } else {
        navigate(`/ordermanagement/residents`);
      }
    }
  }, [orderSuccess, orderError]);

  useEffect(() => {
    if (deleteOrderSuccess) {
      navigate(`/ordermanagement/residents`);
    }
  }, [deleteOrderSuccess]);

  const handleConfirmClose = (canContinue) => {
    setConfirmModalOpen(false);

    if (canContinue) {
      if (isEditing) setIsFinalizingOrder(true);
      addNewOrder(orderInfo);
    }
  };

  const handleReasonClose = (canContinue, reasonText = "") => {
    setReasonModalOpen(false);
    setReasonModalTitle("");

    if (canContinue) {
      setOrderInfo((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          note: reasonText,
        },
      }));
      setIsSubmitAfterReason(true);
    }
  };

  useEffect(() => {
    if (isSubmitAfterReason) {
      addNewOrder(orderInfo);
      setIsSubmitAfterReason(false);
    }
  }, [isSubmitAfterReason]); //due to async order update, have to use a hook

  const getBannerName = () => {
    if (!isEditing) return residentDetail?.name;
    return orderData?.residentName || menuInfo?.residentName;
  };

  const getBannerRoomBed = () => {
    if (!isEditing && residentDetail) return { roomNumber: residentDetail.roomNumber, bed: residentDetail.bed };
    const roomBed = (orderData?.roomBed || menuInfo?.roomBed)?.split("/");
    return { roomNumber: roomBed?.[0], bed: roomBed?.[1] };
  };

  return (
    <>
      <Box
        sx={{
          marginY: ".5rem",
          marginX: "1rem",
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center"
        }}
      >
        <BackButton
          text="Back"
          variant="outlined"
          To="/ordermanagement/residents"
          sx={{
            color: "#01617e",
            borderColor: "#01617e",
            marginRight: "1.5rem"
          }}
        />
      </Box>
      {isResidentLoading ? (
        <EditSkeleton />
      ) : (
        <div className="paperContent customScrollbar">
          <ResidentDetailSection
            residentId={residentId}
            residentInfo={residentInfo}
            diningAreas={diningAreas}
            selectedDiningAreaId={selectedDiningAreaId}
            onDiningAreaChange={selectDiningArea}
            selectedTableName={selectedTableName}
            onTableChange={selectTable}
            outOfBuilding={outOfBuilding}
            onOutOfBuildingChange={handleOutOfBuildingChange}
            declinedMeal={declinedMeal}
            onDeclinedMealChange={handleDeclinedMealChange}
            mealAddons={allowMealAddons ? mealAddons : []}
            menuInfo={menuInfo}
            showPhoto={showPhoto}
            residentName={getBannerName()}
            roomBed={getBannerRoomBed()}
          />

          {setupWarning && (
            <WarningText text={setupWarning} color="red" />
          )}

          {!setupWarning && !menuInfo && !isMenuQuerySkipped && !isMenuLoading && (
            <>
              <WarningText
                text={"Unable to load order. Please ensure the resident is assigned to a dining area before proceeding."}
              />
              <Box display="flex" justifyContent="center" alignItems="center" marginBlock="10px">
                <Button variant="contained" onClick={() => navigate(-1)}>Go Back</Button>
              </Box>
            </>
          )}

          <OrderMenuDetails
            residentId={residentId}
            menuInfo={menuInfo}
            isMenuLoading={isMenuLoading}
            isHidden={!!setupWarning || (!menuInfo && !isMenuQuerySkipped && !isMenuLoading)}
            existingOrder={orderData}
            hasExistingOrder={isEditing}
            orderInfo={orderInfo}
            setOrderInfo={setOrderInfo}
            selectedSubRecipes={selectedSubRecipes}
            setSelectedSubRecipes={setSelectedSubRecipes}
            noteText={noteText}
            setNoteText={setNoteText}
            addlCharges={addlCharges}
            setAddlCharges={setAddlCharges}
            customChargeText={customChargeText}
            setCustomChargeText={setCustomChargeText}
            showAdditionalCharges={showAdditionalCharges}
            setShowAdditionalCharges={setShowAdditionalCharges}
            selectedAddlCharges={selectedAddlCharges}
            setSelectedAddlCharges={setSelectedAddlCharges}
            outOfBuilding={outOfBuilding}
            declinedMeal={declinedMeal}
            isAutoSelectItems={isAutoSelectItems}
            onFluidRestrictionWarning={() => {
              setModalTitle("Fluid Restriction");
              setModalText(
                "This resident has a fluid restriction.\nMake sure that this selection doesn't exceed the resident's restriction."
              );
              setInfoModalOpen(true);
            }}
          />
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100% !important", flex: 1 }}>
            {isEditing && (
              <Button
                variant="contained"
                sx={{
                  fontWeight: "bold",
                  marginRight: ".5rem",
                  width: "calc(50% - 1rem)",
                  backgroundColor: "darkred",
                  color: "#FFF",
                }}
                onClick={() => {
                  // arm before the delete mutation fires so the tag invalidation it triggers doesn't refetch the now-deleted order
                  setIsFinalizingOrder(true);
                  setShowDeleteModal(true);
                }}>
                Delete Order
              </Button>
            )}
            <Button
              variant="contained"
              color="#000"
              sx={{
                fontWeight: "bold",
                width: isEditing ? "calc(50% - 1rem)" : "calc(100% - 2.5rem)",
                backgroundColor: "#fdb92e",
              }}
              onClick={() => handleSubmit()}>
              Submit
            </Button>
          </Grid>
        </div>
      )}
      {infoModalOpen && (
        <InfoModal
          title={modalTitle}
          open={infoModalOpen}
          modalText={modalText}
          handleClose={handleModalClose}
        />
      )}
      {confirmModalOpen && (
        <OrderConfirmModal
          open={confirmModalOpen}
          orderData={orderInfo}
          confirmType="SUBMIT"
          selectedSubRecipes={selectedSubRecipes}
          handleClose={handleConfirmClose}
          isNoCharge={false}
          isGuest={false}
          mealPlans={residentDetail?.mealPlans}
          mealCountUsage={residentDetail?.mealCountUsage}
          mealCostUsage={residentDetail?.mealCostUsage}
          currentMealId={currentMealId}
        />
      )}
      {reasonModalOpen && (
        <ReasonModal
          open={reasonModalOpen}
          title={reasonModalTitle}
          handleClose={handleReasonClose}
        />
      )}
      {showDeleteModal && (
        <DeleteModal
          id={orderData?.order?.id}
          entityName={"this order"}
          apiPath={"deleteOrder"}
          isAdmin={true}
          open={showDeleteModal}
          isafterSuccessFunction={true}
          afterSuccessFunction={() => {
            navigate(`/ordermanagement/residents`);
          }}
          close={(canContinue) => {
            setShowDeleteModal(false);
            if (!canContinue) setIsFinalizingOrder(false); // cancelled - resume normal order fetching
          }}
        />
      )}
    </>
  );
}